import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  compose,
  equals,
  join,
  includes,
  lensPath,
  map,
  set,
  sortBy,
  toLower,
  prop,
  view,
  head,
} from "ramda";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleDown, faAngleRight } from "@fortawesome/pro-regular-svg-icons";
import { MediaContext } from "../../common/media-context";
import { getOptionalFieldsForUpload, submissionTypes } from "./uploadUtils";
import { notBlank } from "../../../util/ext";
import { useIsAdmin } from "../../../auth/authUtils";

import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import AgeRangeSelect from "../../content/edit/AgeRangeSelect";
import AudienceSelect from "../../common/AudienceSelect";
import TextDirectionSelect from "../../content/edit/TextDirectionSelect";
import VideoRatingsSelect from "../../../manage-csp/edit/VideoRatingsSelect";

const useStyles = makeStyles(theme => ({
  icon: {
    "& > :first-child": {
      fontSize: "1.6rem",
      marginRight: 20,
      color: "black",
    },
  },

  btn: {
    paddingLeft: 0,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },

  header: {
    fontSize: "2rem",
    lineHeight: "2.6rem",
    color: "black",
    fontWeight: 700,
  },
}));

const OptionalFields = () => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const isAdmin = useIsAdmin();

  const [optionalFields, setOptionalFields] = useState([]);
  useEffect(() => {
    if (notBlank(context.media.type)) {
      const optionalFields = getOptionalFieldsForUpload(
        context.media.type,
        isAdmin,
      );
      setOptionalFields(optionalFields);
    }
  }, [context.media.type, isAdmin]);

  const [expanded, setExpanded] = useState(false);
  const handleClick = () => setExpanded(!expanded);

  const handleChange = field => event => {
    const value = event.target.value;
    context.setMedia(prev => set(lensPath(field), value, prev));
  };

  const isRequired = field => {
    const { type } = context.media;
    return (
      notBlank(context.requiredFields[type]) &&
      includes(field, context.requiredFields[type])
    );
  };

  return (
    <Accordion expanded={expanded}>
      <AccordionSummary style={{ cursor: "default" }}>
        <Grid container justify="space-between" alignItems="center" spacing={3}>
          <Grid item style={{ paddingLeft: 0 }}>
            <Button
              disableRipple
              onClick={handleClick}
              startIcon={
                <FontAwesomeIcon icon={expanded ? faAngleDown : faAngleRight} />
              }
              classes={{
                root: classes.btn,
                startIcon: classes.icon,
              }}
            >
              <Typography className={classes.header}>
                Optional Fields
              </Typography>
            </Button>
          </Grid>

          <Grid item xs>
            <Divider />
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={4}>
          <Grid item xs={4} style={{ alignSelf: "flex-end" }}>
            <Typography variant="h6">
              {submissionTypes[context.media.type].displayName}
            </Typography>
          </Grid>

          {notBlank(optionalFields) &&
            map(
              optionalField => (
                <Grid
                  item
                  xs={4}
                  key={`content-${join("-", optionalField.fieldName)}`}
                >
                  {equals(optionalField.label, "Text Directionality") ? (
                    <TextDirectionSelect
                      label="Text Directionality (optional)"
                      value={context.media.textDirectionality}
                      onChange={handleChange(["textDirectionality"])}
                    />
                  ) : equals(optionalField.label, "Age Range") ? (
                    <AgeRangeSelect
                      label="Age Range (optional)"
                      value={context.media.ageRange}
                      valueFormat="ageRange"
                      onChange={handleChange(["ageRange"])}
                    />
                  ) : equals(optionalField.label, "Audience") ? (
                    <AudienceSelect
                      label="Audience (optional)"
                      value={context.media.audience}
                      valueFormat="audience"
                      onChange={handleChange(["audience"])}
                    />
                  ) : equals(optionalField.label, "Rating") ? (
                    <VideoRatingsSelect
                      label="Ratings (optional)"
                      value={context.media.rating}
                      valueFormat="rating"
                      onChange={handleChange(["rating"])}
                    />
                  ) : (
                    <TextField
                      id={`content-${join("-", optionalField.fieldName)}`}
                      fullWidth
                      required={isRequired(head(optionalField.fieldName))}
                      label={`${optionalField.label} (optional)`}
                      helperText={
                        optionalField.label === "Keywords"
                          ? "Keywords should be separated by commas"
                          : null
                      }
                      value={
                        view(
                          lensPath(optionalField.fieldName),
                          context.media,
                        ) || ""
                      }
                      onChange={handleChange(optionalField.fieldName)}
                      inputProps={optionalField.inputProps}
                    />
                  )}
                </Grid>
              ),
              sortBy(compose(toLower, prop("label")), optionalFields),
            )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default OptionalFields;
